import { isRealtimePublicChannel } from "@birdwatch/common";
import { RelayProvider } from "@birdwatch/relay/react";
import { useAuth } from "@clerk/clerk-react";
import { type ReactNode, useCallback } from "react";

import { env } from "@/env";

/** Same Clerk template platform and media verify (media-upload keeps its own copy; consolidating is out of scope). */
const RELAY_TOKEN_TEMPLATE = "birdwatch-template";

/**
 * Mounts the one relay connection at the router root ([__root.tsx]). Opens lazily when a hook
 * subscribes, sends a fresh Clerk token as `Authorization: Bearer` on every (re)connect, connects
 * while signed out to the public channels alone, and remounts the connection when the Clerk
 * user changes so the relay always sees the current identity.
 *
 * It is the webapp's only realtime connection: every live update arrives here.
 */
export function RealtimeRelayProvider({ children }: { children: ReactNode }) {
  const { getToken, userId } = useAuth();
  const getRelayToken = useCallback(
    () => getToken({ template: RELAY_TOKEN_TEMPLATE }),
    [getToken],
  );
  return (
    <RelayProvider
      identityKey={userId}
      url={`${env.PUBLIC_REALTIME_URL}/api/realtime`}
      getToken={getRelayToken}
      isPublicChannel={isRealtimePublicChannel}
    >
      {children}
    </RelayProvider>
  );
}
